import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  PaymentSummary, 
  ProviderCompletionForm, 
  ClientConfirmation, 
  DisputeForm, 
  ReviewForm 
} from './completion';
import BookingCharges from './bookings/BookingCharges';
import ServiceTracking from './bookings/ServiceTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingCompletionFlow = ({ booking: initialBooking, userRole: userRoleProp, user, onUpdate, onClose }) => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(initialBooking);
  // Derive the viewer's role when not passed explicitly: the booking's provider
  // is the 'provider', everyone else (the booker) is the 'client'. This guards
  // against callers that forget the userRole prop (otherwise the payment summary
  // would wrongly fall back to the provider "Your Earnings" view for a client).
  const userRole = userRoleProp || (user && initialBooking && user.id === initialBooking.provider_id ? 'provider' : 'client');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  
  // Detailed ratings - average of these 4 criteria
  const [ratings, setRatings] = useState({
    service_quality: 5,
    timeliness: 5,
    attitude: 5,
    value_for_money: 5
  });
  
  // Completion proof images
  const [proofImages, setProofImages] = useState([]);
  const [proofPreviews, setProofPreviews] = useState([]);
  const [uploadingProof, setUploadingProof] = useState(false);

  // Completion proof files (non-image deliverables: PDF, DOC, ZIP, etc.)
  const [proofFiles, setProofFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Live tracking state (bubbled up from ServiceTracking). Used to gate
  // "Request Completion" behind "End Job" when the tracking flow is engaged.
  const [tracking, setTracking] = useState(null);

  // Fetch fresh booking data on mount
  useEffect(() => {
    const fetchFreshBooking = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${initialBooking.id}`);
        setBooking(response.data);
        
        // If booking is already completed, show review form
        if (response.data.status === 'completed' && !response.data.reviewed) {
          setShowReviewForm(true);
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
      }
    };
    fetchFreshBooking();
  }, [initialBooking.id]);

  // Calculate average rating
  const averageRating = Object.values(ratings).reduce((a, b) => a + b, 0) / 4;

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleProofImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (proofImages.length + files.length > 5) {
      setError('Maximum 5 proof images allowed');
      return;
    }

    setUploadingProof(true);
    setError('');

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each file must be less than 5MB');
        continue;
      }

      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Only JPEG, PNG, and WebP images are allowed');
        continue;
      }

      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/service-image`, uploadData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.url) {
          setProofImages(prev => [...prev, response.data.url]);
          
          const reader = new FileReader();
          reader.onloadend = () => {
            setProofPreviews(prev => [...prev, reader.result]);
          };
          reader.readAsDataURL(file);
        }
      } catch (error) {
        console.error('Proof image upload error:', error);
        setError(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
      }
    }

    setUploadingProof(false);
  };

  const removeProofImage = (index) => {
    setProofImages(prev => prev.filter((_, i) => i !== index));
    setProofPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleProofFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (proofFiles.length >= 5) {
      setError('Maximum 5 files allowed');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File must be less than 20 MB');
      return;
    }
    setUploadingFile(true);
    setError('');
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/file`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      if (response.data?.url) {
        setProofFiles(prev => [...prev, {
          url: response.data.url,
          original_name: response.data.original_name,
          size_bytes: response.data.size_bytes,
          content_type: response.data.content_type,
        }]);
      }
    } catch (err) {
      setError(`Failed to upload file: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const removeProofFile = (index) => {
    setProofFiles(prev => prev.filter((_, i) => i !== index));
  };

  const requestCompletion = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/bookings/${booking.id}/request-completion`, {
        notes: completionNotes,
        proof_images: proofImages,
        proof_files: proofFiles,
      });
      setSuccess('Completion request sent to client!');
      onUpdate();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to request completion');
    } finally {
      setLoading(false);
    }
  };

  const confirmCompletion = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/bookings/${booking.id}/confirm-completion`);
      const earnings = response.data.provider_earnings || response.data.service_amount || 0;
      setSuccess(`Job completed! Provider earned ₱${earnings.toFixed(2)}`);
      setShowReviewForm(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to confirm completion';
      
      if (errorMsg.includes('pending completion request') || errorMsg.includes('already')) {
        setSuccess('This booking has already been confirmed! You can now leave a review.');
        setShowReviewForm(true);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const disputeCompletion = async () => {
    if (!disputeReason.trim()) {
      setError('Please select a reason for the dispute');
      return;
    }
    if (!disputeDescription.trim()) {
      setError('Please provide a detailed description');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/disputes`, {
        booking_id: booking.id,
        reason: disputeReason,
        description: disputeDescription
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccess('Dispute opened. Payment has been held. Redirecting to dispute page...');
      setTimeout(() => {
        onUpdate();
        onClose();
        navigate(`/disputes/${response.data.id}`);
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to open dispute');
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/bookings/${booking.id}/review`, {
        ratings: ratings,
        average_rating: averageRating,
        comment: reviewComment
      });
      setSuccess('Review submitted! Thank you for your feedback.');
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completion_requested: 'bg-purple-100 text-purple-800',
      pending_completion: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleSkipReview = () => {
    onUpdate();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="completion-flow-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-4 py-3 border-b bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-white">Booking Details</h3>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Booking Info */}
          <div className="mb-6">
            <h4 className="font-bold text-lg">{booking.service_title}</h4>
            <p className="text-gray-600">
              {userRole === 'provider' ? `Client: ${booking.client_name}` : `Provider: ${booking.provider_name}`}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(booking.status)}`}>
                {booking.status.replace('_', ' ')}
              </span>
            </div>
            
            <PaymentSummary booking={booking} userRole={userRole} />
          </div>

          {/* Live service tracking: On-My-Way, map/ETA, before photos, job timer */}
          <ServiceTracking booking={booking} effectiveRole={userRole} onTrackingUpdate={setTracking} />

          {/* Extra charges (provider add-ons) & tips */}
          <BookingCharges booking={booking} effectiveRole={userRole} />

          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Provider Actions */}
          {userRole === 'provider' && (
            <>
              {(booking.status === 'accepted' || booking.status === 'in_progress') && (
                <ProviderCompletionForm
                  completionNotes={completionNotes}
                  setCompletionNotes={setCompletionNotes}
                  proofPreviews={proofPreviews}
                  proofImages={proofImages}
                  uploadingProof={uploadingProof}
                  onProofUpload={handleProofImageUpload}
                  onRemoveProof={removeProofImage}
                  proofFiles={proofFiles}
                  uploadingFile={uploadingFile}
                  onProofFileUpload={handleProofFileUpload}
                  onRemoveProofFile={removeProofFile}
                  onRequestCompletion={requestCompletion}
                  loading={loading}
                  requestDisabled={
                    !!(tracking?.on_my_way_at ?? booking.on_my_way_at) &&
                    !(tracking?.job_ended_at ?? booking.job_ended_at)
                  }
                />
              )}

              {(booking.status === 'completion_requested' || booking.status === 'pending_completion') && (
                <div className="text-center py-4">
                  <div className="text-purple-600 text-4xl mb-2">⏳</div>
                  <p className="font-semibold">Waiting for Client Confirmation</p>
                  <p className="text-gray-500 text-sm mt-1">The client has been notified to confirm job completion.</p>
                </div>
              )}

              {booking.status === 'disputed' && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-700 mb-2">Completion Disputed</h4>
                  <p className="text-red-600 text-sm">Reason: {booking.dispute_reason}</p>
                  <p className="text-gray-600 text-sm mt-2">Please contact the client to resolve this issue.</p>
                </div>
              )}
            </>
          )}

          {/* Client Actions */}
          {userRole === 'client' && (
            <>
              {(booking.status === 'completion_requested' || booking.status === 'pending_completion') && !showReviewForm && (
                <>
                  <ClientConfirmation
                    booking={booking}
                    onConfirmCompletion={confirmCompletion}
                    onShowDisputeForm={() => setShowDisputeForm(!showDisputeForm)}
                    showDisputeForm={showDisputeForm}
                    loading={loading}
                  />
                  
                  {showDisputeForm && (
                    <DisputeForm
                      disputeReason={disputeReason}
                      setDisputeReason={setDisputeReason}
                      disputeDescription={disputeDescription}
                      setDisputeDescription={setDisputeDescription}
                      onDisputeCompletion={disputeCompletion}
                      onCancel={() => setShowDisputeForm(false)}
                      loading={loading}
                    />
                  )}
                </>
              )}

              {showReviewForm && (
                <ReviewForm
                  ratings={ratings}
                  onRatingChange={handleRatingChange}
                  averageRating={averageRating}
                  reviewComment={reviewComment}
                  setReviewComment={setReviewComment}
                  onSubmitReview={submitReview}
                  onSkip={handleSkipReview}
                  loading={loading}
                />
              )}

              {booking.status === 'completed' && booking.reviewed && (
                <div className="text-center py-4">
                  <div className="text-green-600 text-4xl mb-2">✓</div>
                  <p className="font-semibold">Job Completed & Reviewed</p>
                  <p className="text-gray-500 text-sm mt-1">Thank you for your feedback!</p>
                </div>
              )}
            </>
          )}

          {booking.status === 'completed' && (
            <div className="text-center py-4 bg-green-50 rounded-lg mt-4">
              <div className="text-green-600 text-4xl mb-2">✓</div>
              <p className="font-semibold text-green-700">Job Successfully Completed</p>
              {booking.completed_at && (
                <p className="text-gray-500 text-sm mt-1">
                  Completed on {new Date(booking.completed_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCompletionFlow;
